import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #34: A Map of Global Migrations, Part 3",
  "description": "",
  "published": "2017-02-20T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-34-map-global-migrations-part-3/swizec/7455"],
  "categories": "d3js, Front End, Livecoding, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding ?. Always under 500 words and with pictures. You can follow my channel, `}<a parentName="em" {...{
          "href": "https://liveedu.tv/swizec"
        }}>{`here`}</a>{`. New content almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{`. There’s live chat, come say hai ?`}</em></p>
    <p>{`This Sunday, we built a zoomable pannable map with React and D3. We also added animation to our migration curves. Now people can see what they mean.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/uk-migrations.gif",
        "alt": "Click on the map to see a live example"
      }}></img></p>
    <p>{`See? It zooms and it pans and the circles follow their curves. Just like the color, the amount of circles on each line signifies migration intensity. ?`}</p>
    <p>{`Here's how it works ?`}</p>
    <h2 {...{
      "id": "animate-svg-element-to-follow-a-curve"
    }}>{`Animate SVG element to follow a curve`}</h2>
    <p>{`Moving an element along a line is one of those things you think you can figure out on your own but you can't. Then you see how it's done and you think, `}<em parentName="p">{`"I'm dumb"`}</em>{`.`}</p>
    <p>{`But you're not dumb. It's totally un-obvious.`}</p>
    <p>{`We used Bostock's `}<a parentName="p" {...{
        "href": "https://bl.ocks.org/mbostock/1705868"
      }}>{`Point-Along-Line Interpolation`}</a>{` example as our model. It creates a custom tween that we apply to each circle as an `}<inlineCode parentName="p">{`attrTween`}</inlineCode>{` transition.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function translateAlong(path) {
  var l = path.getTotalLength();
  return function (d, i, a) {
    return function (t) {
      var p = path.getPointAtLength(t * l);
      return "translate(" + p.x + "," + p.y + ")";
    };
  };
}
`}</code></pre>
    <p>{`That's the custom tween. I'd love to explain how it works, but… well, we have a `}<inlineCode parentName="p">{`getPointAtLength`}</inlineCode>{` function that SVG gives us by default on every `}<inlineCode parentName="p">{`<path>`}</inlineCode>{` element. We use it to generate `}<inlineCode parentName="p">{`translate(x, y)`}</inlineCode>{` strings that we feed into the `}<inlineCode parentName="p">{`transform`}</inlineCode>{` attribute of a `}<inlineCode parentName="p">{`<circle>`}</inlineCode>{` element. That part is obvious.`}</p>
    <p>{`The part I don't get is the 3-deep function nesting for currying. The top function returns a tween, I assume. It's a function that takes `}<inlineCode parentName="p">{`d, i, a`}</inlineCode>{` as arguments and never uses them. Instead, it returns a time-parametrized function that returns a `}<inlineCode parentName="p">{`translate(x, y)`}</inlineCode>{` string for each `}<inlineCode parentName="p">{`t`}</inlineCode>{`.`}</p>
    <p>{`We know from D3 conventions that `}<inlineCode parentName="p">{`t`}</inlineCode>{` runs in the `}<inlineCode parentName="p">{`[0, 1]`}</inlineCode>{` interval, so we can assume the tween gives a `}<em parentName="p">{`"point at t percent of full length of path"`}</em>{` coordinates.`}</p>
    <p>{`Great.`}</p>
    <p>{`We apply it in our `}<inlineCode parentName="p">{`MigrationLine`}</inlineCode>{` component like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// inside MigrationLine
    _transform(circle, delay) {
        const { start } = this.props,
              [ x1, y1 ] = start;

        d3.select(circle)
          .attr("transform", \`translate(\${x1}, \${y1})\`)
          .transition()
          .delay(delay)
          .duration(this.duration)
          .attrTween("transform", translateAlong(this.refs.path))
          .on("end", () => {
              if (this.state.keepAnimating) {
                  this._transform(circle, 0);
              }
          });
    }

    componentDidMount() {
        const { Ncircles } = this.props;

        this.setState({
            keepAnimating: true
        });

        const delayDither = this.duration*Math.random(),
              spread = this.duration/Ncircles;

        d3.range(delayDither, this.duration+delayDither, spread)
          .forEach((delay, i) =>
              this._transform(this.refs[\`circles-\${i}\`], delay)
          );
    }
`}</code></pre>
    <p>{`We call `}<inlineCode parentName="p">{`_transform`}</inlineCode>{` on every circle in our component. Each gets a different `}<inlineCode parentName="p">{`delay`}</inlineCode>{` so that we get a roughly uniform distribution of circles on the line. Without this, they come in bursts, and it looks weird.`}</p>
    <p><inlineCode parentName="p">{`delayDither`}</inlineCode>{` ensures the circles for each line start with a random offset, which also helps fight the burstiness. Here's what the map looks like without `}<inlineCode parentName="p">{`delayDither`}</inlineCode>{` and with a constant delay between circles regardless how many there are.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/bursty-map-1.gif",
        "alt": null
      }}></img></p>
    <p>{`See? No good.`}</p>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/migrations-map/blob/de8800aecca4484f8c5827a82c8a746127264143/src/MigrationLine.js"
      }}>{`full MigrationLine code on Github`}</a>{`.`}</p>
    <h2 {...{
      "id": "zoom-and-pan-a-map"
    }}>{`Zoom and pan a map`}</h2>
    <p>{`This part was both harder and easier than I thought.`}</p>
    <p>{`You see, D3 comes with something called `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-zoom"
      }}>{`d3.zoom`}</a>{`. It does zooming `}<em parentName="p">{`and`}</em>{` panning.`}</p>
    <p>{`Cool, right? Should be easy to implement. And it is… if you don't fall down a rabbit hole.`}</p>
    <p>{`In the old days, the standard approach was to `}<inlineCode parentName="p">{`.call()`}</inlineCode>{` zoom on an `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{` element, listen for `}<inlineCode parentName="p">{`zoom`}</inlineCode>{` events, and adjust your scales. Zoom callback would tell you how to change zoominess and where to move, and you'd adjust your scales and re-render the visualization.`}</p>
    <p>{`We tried that approach with hilarious results:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/drag-1.gif",
        "alt": null
      }}></img></p>
    <p>{`First, it was moving in the wrong direction, then it was jumping around. Changing the geo projection to achieve zoom and pan was `}<em parentName="p">{`not`}</em>{` the answer. Something was amiss.`}</p>
    <p>{`Turns out in D3v4, the zoom callback gets info to build an SVG transform. A `}<inlineCode parentName="p">{`translate()`}</inlineCode>{` followed by a `}<inlineCode parentName="p">{`scale()`}</inlineCode>{`.`}</p>
    <p>{`Apply those on the visualization and you get working zooming and panning! Of anything :D`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// in SVG rendering component
    onZoom() {
        this.setState({
            transform: d3.event.transform
        });
    }

    get transform() {
        if (this.state.transform) {
            const { x, y, k } = this.state.transform;
            return \`translate(\${x}, \${y}) scale(\${k})\`;
        }else{
            return null;
        }
    }
    // ...
    render() {
        return (
            <svg width={width} height={height} ref="svg">
                <g transform={this.transform}>
                // ...
    }
</g></svg>
`}</code></pre>
    <p>{`Get `}<inlineCode parentName="p">{`d3.event.transform`}</inlineCode>{`, save it in `}<inlineCode parentName="p">{`state`}</inlineCode>{` or a data store of some sort, re-render, use it on a `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` element that wraps everything.`}</p>
    <p>{`Voila, zooming and panning. ?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/uk-migrations.gif",
        "alt": "Click on the map to see a live example"
      }}></img></p>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/migrations-map/blob/de8800aecca4484f8c5827a82c8a746127264143/src/Maps.js#L69"
      }}>{`full `}<inlineCode parentName="a">{`World`}</inlineCode>{` component on Github`}</a>{`.`}</p>
    <p>{`Happy hacking! ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      